import {
	AppButton,
	Badge,
	Card,
	Checkbox,
	Collapse,
	Divider,
	Label,
	Radio,
	RadioGroup,
	Slider,
	Textarea,
	Toggle,
	Tooltip,
	TooltipActionButton,
	TooltipActions
} from '@clickguard/design-system'
import { AlertTriangle, HelpCircle } from 'components/icons'
import { CSSProperties, ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate } from 'react-router-dom'
import { useFeatureFlag } from 'services/feature-flags'
import {
	isRegionEmpty,
	RegionPicker,
	RegionSelection
} from 'components/country-select/region-picker'
import { useAccountStore, useAssetStore } from 'store'
import { ProtectionLevel, SensitivityMode } from './types'
import { SIGNALS, SignalSpec } from './signals'
import {
	cloneSettings,
	DEFAULT_SETTINGS,
	ProtectionSettings
} from './protection-values'
import { UnsavedChangesGuard } from './unsaved-changes-guard'
import './protection-settings-card.css'

// Static 12px help-circle in text-tertiary, mirroring the Figma "Help Icon"
// (display-only) - the tooltip keeps the copy reachable without changing the
// rendered pixel footprint.
// The trigger has to be focusable for the tooltip to be reachable by keyboard,
// and anything focusable that paints no ring of its own gets Chrome's default
// blue one. `cg-shadow-focus-sm` is the DS ring, straight out of the vendored
// styles.css, so this stays on the token (and its per-theme alpha) instead of
// hard-coding a purple. `rounded-full` keeps the ring circular on a circular
// icon.
/** One signal row: its static copy from `signals.ts` plus this entity's stored state. */
type SignalRow = SignalSpec & { enabled: boolean }

const HELP_FOCUS =
	'rounded-full focus-visible:outline-none focus-visible:cg-shadow-focus-sm'

// Eases the master switch's dim instead of snapping it. Three things forced
// this shape, all measured rather than guessed:
//  - The DS puts `cg-opacity-50` on the control's OWN elements (the toggle
//    track, the checkbox box, the button) so there is no wrapper to animate.
//    The descendant selector is what reaches them.
//  - Those same elements already carry `cg-transition-colors`, which is one
//    class beating one class - and the DS stylesheet loads last, so it wins on
//    source order. `!` is the only way past it, and the property list has to
//    carry the DS's own transitions along or they get dropped: `transform` is
//    the Toggle thumb's slide, the colors are every hover state in here.
//  - `:not(.cg-grid)` steps around the Collapse wrapper nested inside this
//    subtree. Forcing a property list onto it would drop
//    `grid-template-rows` and the whitelist reveal would stop animating.
const LOCK_FADE =
	'transition-opacity duration-200 [&_*:not(.cg-grid)]:!transition-[opacity,color,background-color,border-color,fill,stroke,box-shadow,transform]'

// The DS Tooltip wraps its trigger in an `inline-flex` div it does not expose,
// which shrinks a settings row to the width of its text and drops the tier chip
// out of the right edge. Reach through from the row wrapper - the same move
// `region-picker.tsx` makes for its TwoPaneMenu trigger.
const TOOLTIP_FIT = '[&>div]:w-full'

// Esteban's copy, verbatim (08-11). One constant, because the visible title and
// the Toggle's `aria-label` must stay the same string: the switch has no visible
// text of its own, so this heading IS its name.
const THREAT_MONITORING_TITLE = 'AI-Driven Threat Monitoring'

// The one card serves three scopes: the AI Protection page IS the website, and
// the two drawers mount the same card for one entity. Esteban, 08-13: "the
// message should be customized to wherever the settings is (AI Protection =
// website, drawer type is ad account or campaign)". So the noun is a prop and
// every sentence that points at the thing being protected reads it.
export type ProtectionScope = 'website' | 'ad account' | 'campaign'

// Esteban's copy, verbatim (08-13). The old line named the feature and the
// dashboards, and said nothing about WHOSE traffic stops being flagged. It names
// the entity now, and it is one sentence, because the toggle that turns this
// state on is directly above it.
// Lives out here rather than inline in the JSX so the apostrophe stays plain
// text: a JSX text node runs it past whatever unescaped-entities rule the build
// happens to be carrying.
const detectionOffMessage = (scope: ProtectionScope) =>
	`ClickGuard's AI won't flag bad traffic coming from this ${scope}.`

// The other half of the off state, and the only thing in it the user can still
// act on. Threat monitoring off stops the ANALYSIS, not the connection: the ad
// accounts stay linked, so the clicks keep arriving and custom rules keep
// firing on them. That is what this choice covers, and it is deliberately a
// separate decision from the toggle above - stopping the analysis and cutting
// the feed are two different things, and the feed is what costs quota.
// It stays OUT of the amber block: an alarm fill and a warning triangle over a
// choice the user is free to make either way would read as a second thing going
// wrong.
// The label follows the same noun. On the website it is the ad accounts under it
// that send the clicks, so the plural stays; a drawer holds exactly one entity,
// so it says "this ad account" or "this campaign" and never "your ad accounts".
const keepTrackingLabel = (scope: ProtectionScope) =>
	scope === 'website'
		? 'Keep detecting clicks from your ad accounts'
		: `Keep detecting clicks from this ${scope}`

// The description names two pages, so both are links (Esteban, 08-11). It says
// "your ad accounts stay connected" rather than repeating the label's "clicks
// from your ad accounts": the phrase has to be in this line to carry a link,
// because a link inside the DS label would toggle the checkbox on the way out,
// and the second half is the fact the user needs anyway.
// The two destinations are siblings of this page, so both links are built from
// the current path rather than written out. That is what lets one card serve
// two shells: the preview at /preview/asset/automated-protection and the real
// route at /workspace/:accountId/asset/:assetId/automated-protection both drop
// their last segment to the same base. `ppc-accounts` is the product's own
// shorthand for this asset's ad accounts - it redirects to Settings > Ad
// Accounts with that tab already selected - so nothing here hard-codes a URL
// that Connections owns.
// Built from the ids rather than from `pathname`: the card also renders inside the
// Connections drawers, where the current path is `/settings/workspace/.../connections`
// and a sibling of it is not the page we want.
const assetPath = (accountId: string, assetId: string, page: string) =>
	`/workspace/${accountId}/asset/${assetId}/${page}`

// The SETTINGS tree, which is a different mount point and not a sibling of the one
// above: `/settings` is its own `<Route>` in `main-page.tsx` and the asset pages under
// it are declared with the full `workspace/:accountId/asset/:assetId/...` path again.
// Conversion tracking lives there, so `assetPath` cannot reach it - it built
// `/workspace/.../conversion-tracking`, which matches no route under `AssetBase` and
// fell through to page-not-found.
const settingsPath = (accountId: string, assetId: string, page: string) =>
	`/settings/workspace/${accountId}/asset/${assetId}/${page}`

/**
 * Where "conversion code" points, which depends on a flag.
 *
 * `server_side_tracking_enabled` splits the setup in two: with it ON there is a
 * dedicated Conversion tracking page, and with it OFF that route is not registered at
 * all (`main-page.tsx:301`) and the Settings rail does not offer it
 * (`settings.tsx:113`). The legacy Tracking code page carries the conversion snippet
 * itself in that state - `tracking-section.tsx:173`, a "Conversion tracking" section -
 * so it is the right destination and not a near-miss.
 */
const conversionSetupPath = (
	accountId: string,
	assetId: string,
	serverSideTracking: boolean
) =>
	settingsPath(
		accountId,
		assetId,
		serverSideTracking ? 'conversion-tracking' : 'tracking-code'
	)

// The DS has a token for exactly this and it is not a guess at the brand purple:
// `--cg-text-link` is 85 75 253 in light and 183 178 255 in dark, so the link
// keeps its contrast when the theme flips. Hover deepens to `--cg-brand-hover`,
// which is the pair every other DS brand surface uses. `cg-shadow-focus-sm` is
// the DS ring, the same one HELP_FOCUS uses.
export const INLINE_LINK =
	'rounded-sm font-medium text-[rgb(var(--cg-text-link))] underline decoration-[rgb(var(--cg-text-link))] underline-offset-2 transition-colors hover:text-[rgb(var(--cg-brand-hover))] hover:decoration-[rgb(var(--cg-brand-hover))] focus-visible:outline-none focus-visible:cg-shadow-focus-sm'

// The ticket's wording, verbatim (§1.2). An earlier round said "or tick Worldwide",
// naming the control rather than the action; the ticket names the action, and the copy
// on this page is the ticket's to set.
const REGION_EMPTY_MESSAGE = 'Select at least one country or select Worldwide.'
const REGION_ERROR_ID = 'ai-protection-region-error'

// Says the way out rather than what went wrong, the same as the region message
// above it. "Protection setting" is the wording the section label already uses.
const NO_CHECKS_MESSAGE = 'Turn on at least one protection setting.'
const CHECKS_ERROR_ID = 'ai-protection-checks-error'

// Where these settings apply, and where to go for a narrower scope (Esteban,
// 08-06). It rides the tooltip's `description` slot, not `content`: the DS
// styles content as the SemiBold title and description as the regular
// supporting line, so the answer stays the title and the scope note reads as
// the note it is. Out here rather than inline for the same reason
// DETECTION_OFF_MESSAGE is: an unescaped `>` in a JSX text node is exactly what
// react/no-unescaped-entities exists to catch.
const DETECTION_SCOPE_MESSAGE =
	'These are website-wide settings. If you want to customize it for specific ad accounts or campaigns, navigate to Settings > Ad Accounts.'

// The sensitivity choice (Esteban, 08-11). Automatic hands the whole thing to
// the model: it watches every signal and it sets the level. Custom hands both
// back to the user, and only then does the aggressiveness slider mean anything.
// So the mode owns the toggles below as well as the level. On Automatic every
// signal the plan covers reads ON and none of them is switchable, which is why
// the description says what it watches before it says how strict it is
// (Esteban, 08-11: "when automatic is selected, all toggles should be on").
// The head of the grey box, NOT of the mode choice. The two radios moved out
// above the box on 2026-08-12, so what the box still holds is the level (the
// aggressiveness slider) and the signals (the toggle grid). The title names
// both, in that order, because that is the order they sit in.
const SETTINGS_BOX_TITLE = 'Sensitivity & Signals'
const AUTOMATIC_DESCRIPTION =
	'ClickGuard watches every signal and auto-adjusts how strict the detection is as your traffic changes.'
const CUSTOM_DESCRIPTION =
	'You set how strict the detection is, and you choose which signals it watches.'

// The five stops the slider snaps to. Named rather than numbered: "3" tells the
// user nothing, and the floating tooltip is the only place the current stop is
// spelled out. The end labels say Low and High rather than repeating the first
// and the last name, so no word stands twice on one row.
// The box title names two halves, so each half now says its own name over its own
// block: the slider is the Sensitivity, the toggle grid is the Signals (Esteban,
// 08-12: "maybe we should have the bar as Sensitivity and the toggles with a
// Signals title"). The five stop names below stay a strictness scale, because that
// is what the reader picks: how strict, not how sensitive.
const SENSITIVITY_LABEL = 'Sensitivity'
const SIGNALS_LABEL = 'Signals'
// These five words are the PRODUCT's, not ours. The shipped app names the same
// five stops in `asset/protection/.../detection-mode/detection-mode.tsx`, so this
// card repeats them letter for letter (Esteban, 08-14: "can we keep the same
// naming for the sensitivity options pls"). An earlier round wrote its own scale,
// Lenient to Aggressive, which taught the reader a second vocabulary for one
// control. Never rename these here; rename them in the product first.
const AGGRESSIVENESS_LEVELS = [
	'Quiet',
	'Softer',
	'Normal',
	'Stricter',
	'Aggressive'
]
const aggressivenessName = (value: number) =>
	AGGRESSIVENESS_LEVELS[value - 1] || String(value)
// Wider than the DS default of 374 because the slider now owns a whole column
// rather than an indent, and five words need the room. Measured live, the column
// is 496px at 1440, so 400 leaves a 96px margin. The DS sizes its track with a
// fixed `width` on a `shrink-0` box, so this is a hard number, not a maximum.
const AGGRESSIVENESS_TRACK = 400
// Half the DS thumb. The Slider centres its 18px thumb at
// `9px + (track - 18px) * fraction`, so a tick only sits on its own stop if it
// repeats that same arithmetic. Percentages of the track do NOT land on the
// stops: at five stops they miss by up to 9px.
const THUMB_RADIUS = 9
const aggressivenessTickStyle = (index: number): CSSProperties => {
	const last = AGGRESSIVENESS_LEVELS.length - 1
	// The two end ticks pin to the track's own edges instead of centring on
	// their stop. Centred, "Quiet" would hang past the left edge and
	// "Aggressive" 22px past the right one, so the first word would break the
	// column's left margin and the last would push the card. Pinned, every word
	// stays inside the track and the row still reads end to end.
	if (index === 0) return { left: 0 }
	if (index === last)
		return { left: AGGRESSIVENESS_TRACK, transform: 'translateX(-100%)' }
	return {
		left:
			THUMB_RADIUS + ((AGGRESSIVENESS_TRACK - THUMB_RADIUS * 2) * index) / last,
		transform: 'translateX(-50%)'
	}
}

const Help = ({
	content,
	description,
	label
}: {
	content: string
	description?: string
	label: string
}) => (
	<Tooltip
		content={content}
		description={description}
		position="top"
		showArrow
		portal
	>
		<span
			tabIndex={0}
			role="img"
			aria-label={label}
			className={`inline-flex cursor-help text-[rgb(var(--cg-text-tertiary))] [&>svg]:h-3 [&>svg]:w-3 ${HELP_FOCUS}`}
		>
			<HelpCircle />
		</span>
	</Tooltip>
)

const SectionLabel = ({
	title,
	subtitle,
	help,
	helpDescription,
	helpLabel,
	disabled
}: {
	title: string
	subtitle?: string
	help?: string
	helpDescription?: string
	helpLabel?: string
	disabled?: boolean
}) => (
	// A column, not a row, so a section can carry a subtitle under its title
	// (Esteban, 08-11). `gap-1` is 4px: the pair has to read as one block, and
	// the parent's own `gap-3` still separates it from the control below.
	// Without a subtitle this is a column of one, so the two other sections keep
	// the layout they already had.
	<div className="flex flex-col gap-1">
		<div className="flex items-center gap-1.5">
			{/* `opacity-50` is the DS's own disabled treatment for a Toggle's label
			    (Toggle.tsx `disabled && 'cg-opacity-50'`), so a switched-off section
			    title matches the switched-off rows under it exactly. The help icon
			    keeps full strength on purpose: the copy explaining WHY the section is
			    off is the one thing that must stay readable. */}
			<span
				className={`text-[14px] font-medium leading-[18px] text-[rgb(var(--cg-text-primary))] transition-opacity ${
					disabled ? 'opacity-50' : ''
				}`}
			>
				{title}
			</span>
			{help ? (
				<Help
					content={help}
					description={helpDescription}
					label={helpLabel || title}
				/>
			) : null}
		</div>
		{/* The DS description type verbatim - 12/16 normal on text-secondary - so
		    a section subtitle reads the same as the description under any Radio or
		    Checkbox in this card. It fades with the title, because a live sentence
		    under a greyed-out heading looks like the section is still on. */}
		{subtitle ? (
			<p
				className={`text-[12px] font-normal leading-4 text-[rgb(var(--cg-text-secondary))] transition-opacity ${
					disabled ? 'opacity-50' : ''
				}`}
			>
				{subtitle}
			</p>
		) : null}
	</div>
)

// Radio row with the label block composed OUTSIDE the DS Radio: the DS's own
// `label`/`description` props are string-only, and the Recommended chip has to
// sit inline with the label. Typography is the Toggle's `md` label/description
// verbatim (14/18 medium text-primary over 12/16 normal text-secondary) so
// these rows read as the same kind of row as the Detection settings toggles.
// htmlFor keeps click-to-select.
const RadioOption = ({
	value,
	id,
	label,
	description,
	recommended,
	disabled
}: {
	value: string
	id: string
	label: string
	description: string
	recommended?: boolean
	disabled?: boolean
}) => (
	<div className="flex items-start gap-2">
		{/* The DS Checkbox's own trick, copied: a wrapper the height of ONE line
		    of label so the box centers against the first line instead of the
		    whole label+description block. sm box is 16px, sm line-box is 18px.

		    The three arbitrary variants repair a DS defect (0.33.0). A DISABLED
		    CHECKED Radio drops the brand fill and paints the circle
		    `cg-bg-subtle` grey, and it keeps the white inner dot. A white dot on
		    a light grey circle is all but invisible, so the SELECTED option
		    reads as less selected than the unselected one beside it, which is
		    solid grey. That is unreadable exactly where it matters most: the
		    whole panel is disabled while the entity inherits, and the point of
		    the inherit preview is to SHOW what the parent is set to. So the
		    circle keeps the brand fill and dims as a whole, which is the normal
		    "selected, but not yours to change" look. Report it to the DS. */}
		<span className="flex h-[18px] shrink-0 items-center [&_input:disabled:checked~span>span]:!border-[rgb(var(--cg-brand))] [&_input:disabled:checked~span>span]:!bg-[rgb(var(--cg-brand))] [&_input:disabled:checked~span>span]:opacity-50">
			<Radio value={value} id={id} size="sm" />
		</span>
		{/* The label block dims by hand. The DS puts its own `cg-opacity-50` on the
		    radio BOX when the group is disabled, but this text is composed out here,
		    so nothing fades it - the same reason SectionLabel dims its own title. */}
		<div
			className={`flex flex-col gap-0.5 transition-opacity ${
				disabled ? 'opacity-50' : ''
			}`}
		>
			<span className="flex items-center gap-1.5">
				<label
					htmlFor={id}
					className={`text-[14px] font-medium leading-[18px] text-[rgb(var(--cg-text-primary))] ${
						disabled ? 'cursor-not-allowed' : 'cursor-pointer'
					}`}
				>
					{label}
				</label>
				{/* `Label`, not `Badge`. The DS ships both and its own docstring draws
				    the line: Badge is the marketing-derived flat fill, Label is the
				    product-DS status pill (Figma "Badge", node 148:9498) - 1px brand
				    border + tinted fill + brand text, which is the bordered purple
				    chip in the reference screenshot.
				    `sm` is the DS floor and lands at 22px, which stands taller than
				    the 18px label line-box next to it and pushes the row open. There
				    is no `xs`, so protection-card-label-xs fakes one down to 18px in the
				    co-located CSS - see the note there for why it is not a
				    className. */}
				{recommended ? (
					<Label color="brand" size="sm" className="protection-card-label-xs">
						Recommended
					</Label>
				) : null}
			</span>
			<span className="text-[12px] font-normal leading-4 text-[rgb(var(--cg-text-secondary))]">
				{description}
			</span>
		</div>
	</div>
)

/**
 * The frame the settings sit in.
 *
 * On the AI Protection page that is a grey tray with a white card inside it.
 * In the drawer it is NOTHING (Esteban, 08-11): the settings sit straight on the
 * sheet, and the sheet pads them.
 *
 * A transparent `Card` is NOT the same answer, and Esteban called it out. The box
 * stays in the tree, so its padding stacks under the sheet's own and the content
 * still reads as one thing inside another. The box has to go.
 *
 * `protection-card-settings-tray` survives in both modes: every collapse and chevron
 * rule in `protection-settings-card.css` hangs off that class.
 *
 * Both shadows are DS utilities rather than the hand-written `rgba(16,24,40,…)` pairs
 * they replace. That was a real dark-mode defect, not a lint nicety: a near-black
 * shadow at 3% opacity is invisible on a dark surface, so the tray lost its lift
 * entirely. `cg-shadow-card` is the same two-layer shadow, but its colour and both
 * alphas are theme variables — 16 24 40 at .03/.02 in light, 0 0 0 at .2/.1 in dark,
 * plus a white inset highlight — so it reads in both.
 */
const Frame = ({ bare, children }: { bare: boolean; children: ReactNode }) =>
	bare ? (
		<div className="protection-card-settings-tray">{children}</div>
	) : (
		<Card
			radius="xl"
			padding="none"
			shadow="none"
			className="protection-card-settings-tray cg-shadow-card overflow-hidden !bg-[rgb(var(--cg-canvas))]"
		>
			{/* Header dropped at Esteban's word - the page title above already
			    says "AI Protection", so the card repeated it. The gray band went
			    with it: nothing is left to sit on it, so the white body now fills
			    the tray edge to edge and `border-t` is gone too (it would double
			    the tray's own border along the same line). */}
			<Card
				radius="xl"
				padding="none"
				shadow="none"
				bordered={false}
				className="w-full cg-shadow-sm"
			>
				{children}
			</Card>
		</Card>
	)

export const ProtectionSettingsCard = ({
	bare = false,
	inherited = false,
	scope = 'website',
	saveSlot,
	parentValues = DEFAULT_SETTINGS,
	initialValues = DEFAULT_SETTINGS,
	savedInherited = false,
	asidePanel,
	onSave
}: {
	/**
	 * Render for the ad-account drawer instead of the AI Protection page.
	 *
	 * The page gives the card a grey tray to sit in. The drawer body IS that
	 * tray already, so the tray goes away and what is left reads as one more
	 * `SheetSection`: white, bordered, `rounded-xl`, 16px of padding.
	 *
	 * It also stacks every row. The two-column rows below key off the VIEWPORT,
	 * not the container, so at 1440 they would split a 444px drawer into two
	 * 200px columns and every description would run four lines.
	 */
	bare?: boolean
	/**
	 * The ad account follows its parent, so show the values and let nobody
	 * change them.
	 *
	 * It feeds the same `locked` state the AI master switch feeds, so every
	 * control goes to the DS disabled treatment: greyed, no pointer, out of the
	 * tab order. The first version blocked the controls while they still LOOKED
	 * live, and Esteban said the panel read as frozen, not as inherited
	 * (08-11). The card also drops its own Save button, because there is
	 * nothing on this tab left to save.
	 */
	inherited?: boolean
	/**
	 * What these settings protect, and the noun every sentence in the off state
	 * uses. The AI Protection page leaves it at `website`; the ad-account drawer
	 * passes `ad account` and the campaign drawer passes `campaign`, so the copy
	 * names the entity the drawer opened on rather than the whole site.
	 */
	scope?: ProtectionScope
	/**
	 * Where the Save button goes, when it does not go at the end of the card.
	 *
	 * Esteban, 08-13: "we need the 'save changes' button in the protection tab
	 * be the same sticky component below like in the 'setup' tab so the button
	 * is always visible". So in a drawer the button belongs to the sheet's
	 * pinned footer, and this is the node it lands in. The drawer holds that
	 * node in state and owns the row's alignment; the card owns the button.
	 *
	 * `undefined` keeps the button INSIDE the card, which is what the AI
	 * Protection page wants. Any other value, `null` included, means footer
	 * mode: `null` is only the frame before the drawer's own node has mounted.
	 *
	 * It is a portal and not a lifted callback because the whole save state
	 * (`dirty`, `saving`, `saved`, and the two validation gates) lives in this
	 * card. A footer that has to be told all five holds a second copy of the
	 * state, and two copies drift.
	 */
	saveSlot?: HTMLElement | null
	/**
	 * What this entity shows while it inherits: the effective settings of the
	 * level above it.
	 *
	 * The chain is website, then ad account, then campaign (Esteban, 08-13: "Go
	 * with that: website then account then campaign"). So an ad account gets the
	 * website set, and a campaign gets its ad account's effective set, which is
	 * the website set again whenever that ad account inherits too.
	 *
	 * The card never mutates it. It reads the parent's fields straight out for
	 * display, and it copies the set whenever a draft has to start from it.
	 */
	parentValues?: ProtectionSettings
	/**
	 * What the draft starts at. The entity's own saved settings when it has some,
	 * and a copy of the parent when it has none.
	 *
	 * Read ONCE, at mount. The caller remounts the card (a key on the entity and
	 * the open count) whenever the seed has to change, which is also what drops a
	 * draft the user never saved.
	 */
	initialValues?: ProtectionSettings
	/**
	 * What the STORE says right now: `true` while this entity has no settings of
	 * its own. The tick above the card is the live answer, and this is the saved
	 * one, so the two disagree exactly while the inherit decision itself is
	 * unsaved. That is what puts Save back in reach after a tick with nothing
	 * else touched.
	 */
	savedInherited?: boolean
	/**
	 * Commit. `null` means this entity inherits from now on, and a set of values
	 * means it owns them.
	 *
	 * Esteban set the rule on 08-13: "When the 'save changes' button is pressed
	 * tho, it resets and overrides forever. If it inherits then that's it and if
	 * it's custom then that's it, no more previewing." So a save with the box
	 * ticked also drops the draft this card was holding, and a later untick
	 * starts again from the parent.
	 */
	/**
	 * Rendered beside the monitoring blocks. The AI Protection page passes the
	 * "{n} with their own settings" panel; the drawers pass nothing, since a single
	 * ad account or campaign has no list of entities below it to show.
	 */
	asidePanel?: ReactNode
	/**
	 * Returning a promise is the supported shape: the card AWAITS this, and a rejection
	 * is what stops the button claiming "Saved". Every caller's handler is async.
	 */
	onSave?: (next: ProtectionSettings | null) => void | Promise<void>
} = {}) => {
	const navigate = useNavigate()
	// Demo reads the seeded asset, as every other page on this route does.
	const demoMode = useAssetStore((state) => state.demoMode)
	const asset = useAssetStore((state) => state.asset)
	const accountId = demoMode ? 'workspace-demo' : asset?.account_id ?? ''
	const assetId = demoMode ? 'website-demo' : asset?.id ?? ''
	// The DRAFT. Nine values, and every one of them is what this entity would
	// have if it stopped inheriting.
	//
	// The setter names carry no `own`, on purpose: a derived const under the
	// ORIGINAL name sits below each pair, and that is what the panel reads. So
	// the 1300 lines under here are untouched, and the tick decides which of the
	// two sets they see.
	//
	// It is also why the draft survives a tick. Every control on the panel is
	// disabled while `inherited`, so nothing can write into these while the
	// parent's values are the ones on screen (Esteban, 08-13: "If I check the
	// 'inherit' again I can see the options from the website, uncheck it and see
	// the options you just did").
	const [ownAiEnabled, setAiEnabled] = useState(initialValues.aiEnabled)
	// UNTICKED by default (Esteban, 08-11). Switching the monitoring off is the
	// user's decision to stop, so the card does not carry the click feed on for
	// them. Keeping the feed is the extra choice, and they make it here.
	const [ownKeepTracking, setKeepTracking] = useState(
		initialValues.keepTracking
	)
	const [ownRegion, setRegion] = useState<RegionSelection>(initialValues.region)
	// Defaults to the Recommended option now that it leads the list.
	const [ownProtectionLevel, setProtectionLevel] = useState<ProtectionLevel>(
		initialValues.protectionLevel
	)
	// Automatic by default, and it carries the Recommended chip: the whole promise
	// of the model is that it picks the level for you. Custom is the opt-out, and it
	// is the only state in which the aggressiveness slider means anything.
	const [ownSensitivityMode, setSensitivityMode] = useState<SensitivityMode>(
		initialValues.sensitivityMode
	)
	// The middle stop, so a user who switches to Custom starts where Automatic most
	// often lands rather than at one end of the scale.
	const [ownAggressiveness, setAggressiveness] = useState(
		initialValues.aggressiveness
	)
	const [ownChecks, setChecks] = useState(initialValues.checks)
	const [ownWhitelistOn, setWhitelistOn] = useState(initialValues.whitelistOn)
	const [ownWhitelist, setWhitelist] = useState(initialValues.whitelist)
	const [dirty, setDirty] = useState(false)
	const [saving, setSaving] = useState(false)
	const [saved, setSaved] = useState(false)

	// What the panel shows. The parent's values while the box is ticked, the
	// draft while it is not. Nothing below this line knows which one it got.
	const shown = inherited ? parentValues : null
	const aiEnabled = shown ? shown.aiEnabled : ownAiEnabled
	const keepTracking = shown ? shown.keepTracking : ownKeepTracking
	const region = shown ? shown.region : ownRegion
	const protectionLevel = shown ? shown.protectionLevel : ownProtectionLevel
	const sensitivityMode = shown ? shown.sensitivityMode : ownSensitivityMode
	const aggressiveness = shown ? shown.aggressiveness : ownAggressiveness
	const checks = shown ? shown.checks : ownChecks
	const whitelistOn = shown ? shown.whitelistOn : ownWhitelistOn
	const whitelist = shown ? shown.whitelist : ownWhitelist

	// The AI Protection toggle is the master switch: with it off, nothing under
	// it does anything, so every control below goes disabled rather than sitting
	// there accepting edits that cannot take effect. The Save button stays live -
	// turning protection OFF is itself the change you came to save.
	// Two ways in, one state out. `inherited` joins the master switch here
	// rather than blocking the panel from the outside, because a control that
	// LOOKS live and does nothing reads as broken (Esteban, 08-11). Every row
	// below already follows `locked`, so it now greys out, refuses the pointer
	// and drops out of the tab order the same way it does when the AI switch is
	// off, and the DS says all of that in its own disabled treatment.
	const locked = !aiEnabled || inherited

	// The Save row STAYS, ticked or not. It used to leave while the entity
	// inherited, because there was nothing under it to save. Esteban's rule of
	// 08-13 gave it something: the tick itself is a change, and Save is what
	// commits it ("If it inherits then that's it"). A bar that vanished at the
	// moment of the decision would take the only way to record the decision with
	// it.

	// Gated PER SIGNAL off the account's real features, not off a plan name: the
	// API guards each signal with its own `hasFeature` key (`assets-api.ts:688-700`),
	// and custom plans carry an arbitrary subset. `hasFeature` returns true for every
	// trial, so trials correctly see no chips.
	const hasFeature = useAccountStore((state) => state.hasFeature)
	// The two conversion signals read nothing until the code is on the site.
	const ccInstalled = useAssetStore((state) => !!state.asset?.cc_installed)
	// Decides WHICH page the "conversion code" link opens - see `conversionSetupPath`.
	const serverSideTracking = useFeatureFlag('server_side_tracking_enabled')

	const planCovers = (check: SignalRow) =>
		!check.feature || hasFeature(check.feature)

	// Unticking Worldwide with no countries picked is now reachable (08-04), so
	// the field can hold nothing at all. Not an error while the master switch is
	// off: everything under it is greyed out and unreachable, and turning
	// protection OFF is itself a change you are allowed to save.
	const regionEmpty = !locked && isRegionEmpty(region)

	// Automatic owns the signals as well as the level, so every row it can reach
	// reads ON and none of them is switchable. The user's own picks stay in
	// `checks` untouched and come back the moment Custom does - switching modes
	// twice must not cost the user the set they built.
	const autoSensitivity = sensitivityMode === 'automatic'

	// A signal is dead while the master switch is off, and dead again on
	// Automatic, where the model chooses. The plan gate is separate and it sits
	// on the row itself: it can kill a Pro row inside a mode that is otherwise
	// live.
	const signalsLocked = locked || autoSensitivity

	// A row this account cannot use at all. Two things put it here and both sit
	// outside this drawer: the plan does not carry the row, or the row reads a
	// conversion code the site has not installed. Either way the switch is dead
	// and the tier chip beside it is the reason, so a `Pro` chip never sits next
	// to a switch you are allowed to flip. Esteban, 08-12: "These 2 should be
	// blocked if the Pro tag is shown. That means the user is not on a pro plan
	// and cant use the feature." Before this the two conversion rows kept the
	// chip and stayed live on Pro, which said Pro and then let you use it.
	const unavailable = (check: SignalRow) =>
		!planCovers(check) ||
		(check.needsConversionTracking === true && !ccInstalled)

	// On Automatic every row the plan covers is on, so an unavailable row is the
	// one thing that stays off: the gate outranks the mode, and the tier chip
	// beside it is what says why.
	const signalOn = (check: SignalRow) => {
		if (unavailable(check)) return false
		return autoSensitivity ? true : check.enabled
	}

	// `SIGNALS` order, NOT sorted by tier: with real plan data the chip only shows on
	// a row the plan misses, so a tier sort would reshuffle the grid the moment a
	// customer upgrades. The copy lives in `signals.ts`; settings carry id + enabled.
	// Declared up here because the save validation below reads it too.
	const orderedChecks: SignalRow[] = SIGNALS.map((signal) => ({
		...signal,
		enabled: !!checks.find((check) => check.id === signal.id)?.enabled
	}))

	// Nothing is detecting. Measured with `signalOn` — the SAME predicate the rows
	// render with — never the raw stored `enabled`.
	//
	// Those two disagree, and the difference is the whole bug this replaced: a signal
	// the plan does not cover is forced off on screen by `unavailable()`, but its
	// stored value can still be `true` (an account whose plan lapsed, or a website
	// created before the gate). Reading the stored value let ten visibly-off toggles
	// satisfy "at least one signal on", so Save stayed live on a website that would
	// detect nothing — which is exactly the state §1.4 exists to block. A gated signal
	// stored `true` is not protection: the API rejects it and `alignAccount` rewrites
	// it to false.
	//
	// Suppressed while the master switch is off, for the same reason the region error
	// is - the whole box is greyed out and unreachable down there. Also suppressed on
	// Automatic, where the rows are on by definition and the user cannot empty them.
	const noChecksOn =
		!locked &&
		!autoSensitivity &&
		orderedChecks.every((check) => !signalOn(check))

	const touch = () => {
		setDirty(true)
		setSaved(false)
	}

	// The tick is a change too, and it is the one change no control below can
	// report: the box sits ABOVE this card, and while it is on every control
	// under it is disabled. So the card compares the live tick against the saved
	// one instead, and Save comes back to life the moment they differ.
	const inheritDirty = inherited !== savedInherited

	// Something to commit. A draft edit counts even while the box is ticked: the
	// save is what throws that draft away, so it must be reachable.
	const pending = dirty || inheritDirty

	const draft = (): ProtectionSettings => ({
		aiEnabled: ownAiEnabled,
		keepTracking: ownKeepTracking,
		region: ownRegion,
		protectionLevel: ownProtectionLevel,
		sensitivityMode: ownSensitivityMode,
		aggressiveness: ownAggressiveness,
		checks: ownChecks,
		whitelistOn: ownWhitelistOn,
		whitelist: ownWhitelist
	})

	// The commit, and the end of the preview. Esteban, 08-13: "When the 'save
	// changes' button is pressed tho, it resets and overrides forever. If it
	// inherits then that's it and if it's custom then that's it, no more
	// previewing."
	//
	// Ticked: the entity inherits from now on AND the draft is gone. The nine
	// own values go back to a copy of the parent, so a later untick starts from
	// the parent rather than resurrecting what the user just discarded.
	//
	// Unticked: the draft IS the settings from now on, so there is nothing to
	// reset.
	const commit = async () => {
		if (!inherited) {
			await onSave?.(draft())
			return
		}
		// The write goes FIRST, and the draft is only discarded once it succeeded — a
		// rejected save must leave the user exactly what they had to retry with.
		await onSave?.(null)
		const fresh = cloneSettings(parentValues)
		setAiEnabled(fresh.aiEnabled)
		setKeepTracking(fresh.keepTracking)
		setRegion(fresh.region)
		setProtectionLevel(fresh.protectionLevel)
		setSensitivityMode(fresh.sensitivityMode)
		setAggressiveness(fresh.aggressiveness)
		setChecks(fresh.checks)
		setWhitelistOn(fresh.whitelistOn)
		setWhitelist(fresh.whitelist)
	}

	// `Saved` has to mean SAVED. This used to flip the label on a fixed 650ms timer and
	// fire `commit()` without awaiting it, so a rejected request left the button reading
	// "Saved" behind the error toast — seen live against the demo asset, where the PATCH
	// answers "Asset not found". The store rethrows after raising that toast
	// (`asset-store.ts:1077`), so awaiting is all it takes to tell the two apart: the
	// spinner now runs for the real request, and `dirty` survives a failure so the user
	// can retry.
	const handleSave = async () => {
		setSaving(true)
		setSaved(false)
		try {
			await commit()
			setDirty(false)
			setSaved(true)
		} catch {
			// The store owns the message; this only has to not claim success.
		} finally {
			setSaving(false)
		}
	}

	// One button, two homes. The AI Protection page keeps it on the title line.
	// A drawer portals it into the sheet's pinned footer, so it stays on screen
	// however far the panel scrolls (Esteban, 08-13). Same element, same state,
	// so the two homes can never disagree about what Save can do.
	const saveButton = (
		<AppButton
			label={saved && !pending ? 'Saved' : 'Save changes'}
			variant="primary"
			size="md"
			roundness="default"
			isLoading={saving}
			disabled={!pending || regionEmpty || noChecksOn}
			onClick={handleSave}
		/>
	)

	// The button and the reason it is dead, as one row: "the Save button is disabled
	// with an error message next to it" (§1.4). The message travels WITH the button
	// rather than sitting under the signal grid, so wherever the row lands — the
	// title line on the page, the pinned footer in a drawer — a disabled Save always
	// carries its own explanation.
	// `flex-wrap` for the drawer, where 444px cannot hold both on one line.
	const saveRow = (
		<div className="flex flex-wrap items-center justify-end gap-3">
			{noChecksOn && (
				<p
					id={CHECKS_ERROR_ID}
					role="alert"
					className="text-right text-[13px] leading-[18px] text-[rgb(var(--cg-danger))]"
				>
					{NO_CHECKS_MESSAGE}
				</p>
			)}
			{saveButton}
		</div>
	)

	const toggleCheck = (id: string) => {
		setChecks((current) =>
			current.map((check) =>
				check.id === id ? { ...check, enabled: !check.enabled } : check
			)
		)
		touch()
	}

	const columnLength = Math.ceil(orderedChecks.length / 2)
	const checkColumns = [
		orderedChecks.slice(0, columnLength),
		orderedChecks.slice(columnLength)
	]

	const renderCheck = (check: SignalRow) => {
		const checkDisabled = signalsLocked || unavailable(check)
		// The two conversion checks carry a second gate behind the plan: they read
		// nothing until the conversion code is on the site. A `Setup` button said so
		// in a slot that also holds the tier chip, and a button that neither turns
		// the row on nor explains it reads as the row's main action, which it is not
		// (Esteban, 08-12). The requirement moves into the sentence that already
		// describes the check, and the words that name the requirement are the route
		// to it. That frees the slot to say `Pro` on every plan.
		const needsCode = check.needsConversionTracking === true
		const codeNoteId = `ai-protection-signal-${check.id}-note`
		const row = (
			<div className="flex w-full items-center justify-between gap-3">
				{/* A column, so the hand-built description below sits under the label
				    rather than beside the whole Toggle. On every other row the column
				    holds one child and changes nothing. */}
				<div className="flex min-w-0 flex-col">
					<Toggle
						// `signalOn`, not `check.enabled`: on Automatic the model owns the
						// signals, so every row the plan covers reads ON. `checks` still holds
						// the user's own Custom picks, untouched, and Custom shows them again.
						checked={signalOn(check)}
						onChange={() => toggleCheck(check.id)}
						label={check.label}
						// The description leaves the DS prop on these two rows only, and
						// either reason alone forces it: the prop is typed `string`, so a
						// link cannot go in it, and the DS renders it INSIDE the <label>,
						// where a click on the link would flip the switch as well.
						description={needsCode ? undefined : check.description}
						// The description is no longer the DS's to announce, so the switch
						// points at it by hand. The DS spreads the rest props onto the
						// native input, which is the node that carries the role.
						aria-describedby={needsCode ? codeNoteId : undefined}
						disabled={checkDisabled}
						size="md"
						// The DS top-aligns the track the moment a description exists and
						// sets an 8px gap; the track reads better centered against the two
						// lines with a little more air. `!` is needed because the DS classes
						// are prefixed (cg-items-start, cg-gap-2), so twMerge never sees the
						// two as the same property.
						className="min-w-0 !items-center !gap-3"
					/>
					{needsCode ? (
						// `pl-[42px]` is the md track (30px) plus the row's `!gap-3` (12px),
						// so the text keeps the exact left edge the DS description had.
						// The type copies the DS description verbatim: 12px/16px,
						// font-normal, text-secondary, and the same 50% when the row is off.
						// The DS trims no trailing stop of its own, so the source sentence
						// gives its period back and the parenthesis takes it.
						<p
							id={codeNoteId}
							className={`pl-[42px] text-[12px] font-normal leading-4 text-[rgb(var(--cg-text-secondary))] transition-opacity ${
								checkDisabled ? 'opacity-50' : ''
							}`}
						>
							{check.description.replace(/\.$/, '')} (requires{' '}
							<Link
								to={conversionSetupPath(accountId, assetId, serverSideTracking)}
								className={INLINE_LINK}
							>
								conversion code
							</Link>{' '}
							installation).
						</p>
					) : null}
				</div>
				{/* One slot at the end of the row, and it holds the tier chip. The chip
				    and the dead switch now come from one condition, so the chip is
				    always the reason the row is dead and never a note beside a row you
				    can use. That also takes the `Standard` chip off the Standard plan,
				    where it sat next to four live switches.
				    `shrink-0` because the row is a flex line whose text column carries
				    `min-w-0` - without it this slot is what gives way first. The chip
				    dims by hand: it is not a control, so nothing fades it for us. */}
				{check.tier && !planCovers(check) ? (
					<span
						className={`shrink-0 transition-opacity ${
							signalsLocked ? 'opacity-50' : ''
						}`}
					>
						<Badge variant="brand" size="sm">
							{check.tier}
						</Badge>
					</span>
				) : null}
			</div>
		)

		// The tooltip belongs to the gate, so it goes when the gate goes. On Pro
		// and Custom every row is switchable and there is nothing to explain; on
		// Standard only the Pro rows are dead, and a dead switch with no reason
		// beside it is the thing the tooltip is here to fix.
		if (planCovers(check)) return <div key={check.id}>{row}</div>

		return (
			<div key={check.id} className={TOOLTIP_FIT}>
				<Tooltip
					content={`${check.tier} feature`}
					description={
						<>
							Upgrade your plan to {check.tier} to turn this on.
							{/* Upgrade is the only action the gate can offer. Setup is a step you
							    take after the check is on, so it waits for the plan and then
							    appears on the row itself. */}
							<TooltipActions>
								<TooltipActionButton
									variant="primary"
									label="Upgrade plan"
									onClick={() =>
										navigate(`/settings/workspace/${accountId}/billing`)
									}
								/>
							</TooltipActions>
						</>
					}
					position="top"
					showArrow
					portal
				>
					{row}
				</Tooltip>
			</div>
		)
	}

	return (
		<>
			{/* The SAME predicate the Save button is enabled on (§1.4): nothing is applied
		    until Save is pressed, so anything Save would commit is something leaving
		    would lose. That includes an untouched panel whose inherit tick was flipped —
		    the decision is unsaved even though no control below it moved. */}
			<UnsavedChangesGuard when={pending} />
			<Frame bare={bare}>
				{/* The stack. On the page the frame above pads it; in the drawer
				    there is no frame, so the drawer pads it. */}
				<div className={`flex flex-col gap-4 ${bare ? '' : 'p-4'}`}>
					<div className="flex flex-col gap-3">
						{/* `inherited`, not `locked`: this heading names the master switch
					    below it, and that switch stays usable while the switch is off.
					    It only goes out when the whole panel belongs to the parent. */}
						{/* §1.4's "Save changes at the bottom of the page" describes the flow,
					    not the position: the prototype and the shipped protection panel both
					    keep Save on the title line. A drawer still portals it to its footer.
					    `items-start` holds it against the title, not the title+subtitle block. */}
						<div className="flex items-start justify-between gap-4">
							<SectionLabel
								title={THREAT_MONITORING_TITLE}
								subtitle="ClickGuard's machine learning model evaluates and flags every ad click automatically."
								disabled={inherited}
							/>
							{saveSlot === undefined ? saveRow : null}
						</div>
						<div className="flex items-center gap-2">
							{/* The visible text is the STATE, not the name - the section title
						    right above already names it, so spelling out "Enable AI
						    Protection" again said the same word three times in two lines.
						    `aria-label` carries the name instead, because a switch
						    announced as "On, switch, on" tells a screen reader nothing
						    about what it switches. It repeats the visible title word for
						    word: WCAG 2.5.3 asks the accessible name to CONTAIN the visible
						    label, so a voice user can say what they read. */}
							<Toggle
								id="ai-protection-enabled"
								size="md"
								checked={aiEnabled}
								aria-label={THREAT_MONITORING_TITLE}
								// The master switch cannot follow `locked`: it IS half of
								// `locked`, so it would switch itself off for good. It follows
								// the other half instead, because an inherited account does not
								// own this switch either.
								disabled={inherited}
								onChange={() => {
									setAiEnabled((on) => !on)
									touch()
								}}
							/>
							{/* The help icon next to this label is gone: it held the same
						    sentence the subtitle now says in the open, so it made the user
						    hover for text already on screen. */}
							<label
								htmlFor="ai-protection-enabled"
								className={`text-[14px] font-medium leading-[18px] text-[rgb(var(--cg-text-secondary))] transition-opacity ${
									inherited ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
								}`}
							>
								{aiEnabled ? 'On' : 'Off'}
							</label>
						</div>
						{/* The protective actions only exist while detection is on - off IS
					    the third state, so it never needs a radio of its own. The fill
					    is bg/CANVAS (#F6F6F6), one step down the light ladder
					    (surface #FFF > subtle #FAFAFA > canvas #F6F6F6 > surface-2
					    #F2F2F2) after #FAFAFA read too close to the card behind it. It
					    is what makes the choice read as one object instead of two loose
					    rows hanging off the toggle. Runs the full width of the card at
					    Esteban's word - it no longer shares the 360px cap the Advertising
					    region field below keeps. */}
						{/* DS `Collapse`, not a hand-rolled height animation: it already owns
					    the grid `0fr -> 1fr` idiom, the forced reflow that makes the FIRST
					    open animate, `useInert` so a shut panel leaves the tab order, and
					    the `prefers-reduced-motion` bail-out.
					    The `-mt-3` cancels the parent's `gap-3` and the child re-adds it as
					    `mt-3` INSIDE the clip. Without that, the 12px gap would still be
					    there while the panel plays down to nothing and then vanish in one
					    frame on unmount - a snap at the exact moment the eye is on it. */}
						{/* Two columns on the page, one in the drawers: the own-settings panel is a
					    website-level thing, so `asidePanel` is empty at ad-account and campaign
					    level and the blocks below run full width, which is what they did before
					    the slot existed. `items-stretch` is what lets the panel match the height
					    of the box opposite instead of hugging its own rows. */}
						<div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
							<div className="flex min-w-0 flex-1 flex-col gap-3">
								<Collapse open={aiEnabled} className="-mt-3">
									<div className="mt-3 w-full rounded-lg bg-[rgb(var(--cg-canvas))] p-3">
										{/* This block needed no `locked` until inheritance arrived: the
									    Collapse around it only opens while the AI switch is ON, and
									    `locked` used to mean exactly the opposite. An inherited
									    account keeps the switch on, so the panel is open AND locked,
									    and every control in it must say so. */}
										<span
											className={`mb-1.5 block text-base font-medium text-[rgb(var(--cg-text-primary))] transition-opacity ${
												locked ? 'opacity-50' : ''
											}`}
										>
											Protective Actions
										</span>
										<RadioGroup
											name="protection-level"
											value={protectionLevel}
											disabled={locked}
											onValueChange={(value) => {
												setProtectionLevel(value as ProtectionLevel)
												touch()
											}}
										>
											<RadioOption
												value="full"
												id="protection-full"
												label="Block attackers and unwanted traffic"
												recommended
												description="Monitors and blocks bad traffic sources automatically."
												disabled={locked}
											/>
											<RadioOption
												value="monitoring"
												id="protection-monitoring"
												label="Monitoring only"
												description="Allows you to monitor your traffic from our dashboards, without blocking any bad source."
												disabled={locked}
											/>
										</RadioGroup>
									</div>
								</Collapse>
								{/* Off is a real state, not an absence, so it says what it costs.
							    Same slot and same geometry as the Protective Actions block above
							    - `rounded-lg p-3`, one `-mt-3`/`mt-3` pair - so the panel swaps
							    contents without changing shape. It is deliberately NOT the DS
							    `Banner`: that is page-level chrome at `rounded-2xl p-6`, twice the
							    padding of the p-4 card it would be sitting inside.
							    Warning tone rather than the neutral canvas fill the sibling uses:
							    the user meant to switch this off, but "nothing is analyzed, nothing
							    is blocked" is a consequence, and a #FFFCF0 fill is so close to the
							    white card that without the amber border it reads as nothing at
							    all. */}
								<Collapse open={!aiEnabled} className="-mt-3">
									{/* Two voices, one state. The amber block says what stops; the
								    checkbox under it is what keeps running, and it is the user's
								    to switch off. `items-start` so each one hugs its own text
								    instead of stretching to the card, and gap-3 keeps the pair
								    reading as one answer while giving the control its own room. */}
									<div className="mt-3 flex flex-col items-start gap-3">
										<div className="flex w-fit max-w-full items-start gap-2 rounded-lg border border-[rgb(var(--cg-warning-dark))] bg-[rgb(var(--cg-warning-50))] p-3">
											<span className="mt-px shrink-0 text-[rgb(var(--cg-warning-dark))] [&>svg]:h-4 [&>svg]:w-4">
												<AlertTriangle />
											</span>
											{/* The block hugs its sentence (Esteban, 08-04) rather than
										    stretching to the card: full width left a long empty tail on
										    the second line and read as an unfinished row.
										    `w-fit` alone would take the max-content width - ~1100px as a
										    single line, twice a readable measure - so `max-w-[68ch]` on
										    the text does the real work: it caps the measure, the box then
										    shrinks to exactly that, and `max-w-full` keeps it inside the
										    card if the card is ever narrower. */}
											<span className="max-w-[68ch] text-[13px] leading-[18px] text-[rgb(var(--cg-text-primary))]">
												{detectionOffMessage(scope)}
											</span>
										</div>
										{/* DS `Checkbox` at `sm`, which is the size the Detection
									    settings rows below already use, so the two read as the same
									    kind of control. `label` stays its own string prop, so the
									    label still toggles the box.
									    NO `max-w-[68ch]` here, unlike the amber block above it
									    (Esteban, 08-11): the 68ch measure broke the description onto
									    a second line that held two words, and a two-word orphan under
									    a one-line label reads as a wrap, not as a paragraph. The
									    sentence is 114 characters and the card is wider than it, so
									    letting it run keeps it one line. */}
										{/* This wrapper is a flex column, and it has to be one. The DS
									    `Checkbox` renders its <label> as `inline-flex`, so in a plain
									    block the label sits on a line box, and its own BASELINE sets
									    that line box height. The baseline comes from the box span,
									    which is an EMPTY flex container when unchecked - a flex
									    container with no item takes its baseline from its bottom
									    border edge - and holds the 10px check SVG when checked, whose
									    bottom edge sits 3px higher. So the line box measured 22.5px
									    unchecked and 20px checked: the label rose 0.5px and this
									    description dropped 2.5px on every click, which is the text
									    that moved for no reason (Esteban, 08-13). A flex column takes
									    the label off the line box, so nothing the checkbox draws
									    inside itself can push the text around it.
									    The column carries NO gap on purpose: 0 is the exact distance
									    the DS puts between its own label and its own description, and
									    this paragraph is a copy of that description. */}
										<div className="flex flex-col items-start">
											<Checkbox
												size="sm"
												id="keep-tracking-clicks"
												checked={keepTracking}
												label={keepTrackingLabel(scope)}
												onChange={() => {
													setKeepTracking((on) => !on)
													touch()
												}}
											/>
											{/* The description leaves the DS `description` prop, and either
										    reason alone forces it: the prop is typed `string`, so a link
										    cannot go in it, and the DS renders it INSIDE the <label>,
										    where a click on a link would toggle the checkbox too. So it
										    moves out here and copies the DS's own description type
										    verbatim - 12px/16px, font-normal, text-secondary. `pl-6` is
										    the 16px box plus the DS `gap-2`, so the text keeps the exact
										    left edge it had inside the component.
										    The second half names the entity now (Esteban, 08-13) and it says
										    what the rules keep DOING, not that they "keep working". The old
										    sentence linked "ad accounts" here; the noun is the thing you are
										    already looking at, so a link would send you to the page you are
										    on. One link is left, and it is the one that leads somewhere:
										    Custom Rules.
										    The possessive uses a CURLY apostrophe. A straight quote in a JSX
										    text node is what react/no-unescaped-entities stops. */}
											<p className="pl-6 text-[12px] font-normal leading-4 text-[rgb(var(--cg-text-secondary))]">
												Check this box if you want your{' '}
												<Link
													to={assetPath(accountId, assetId, 'custom-rules')}
													className={INLINE_LINK}
												>
													Custom Rules
												</Link>{' '}
												to keep flagging and blocking threats.{' '}
												{/* NO hard break here, and that is on purpose. The AI Protection
											    PAGE carries a `<br />` at this exact spot, because its column
											    is wide enough that the spend sentence otherwise began mid-line
											    and read as a tail of the Custom Rules sentence. The drawer is
											    narrower, so the text already wraps here on its own, and the
											    break only added a short ragged line. His call on 08-13: the
											    drawer is a special case, no break. */}
												This {scope}’s spend will still count towards your
												monthly usage.
											</p>
										</div>
									</div>
								</Collapse>
							</div>
							{asidePanel ? (
								// The 134px floor exists for the OFF state: the radios box already
								// measures 134, but the warning block measures 106 and the panel used
								// to lose 28px of scroll area the moment the switch was flicked.
								//
								// `empty:hidden` because the ternary above CANNOT answer this: the page
								// always passes an <OwnSettingsList />, which is a truthy element even
								// on the websites where it renders null (nothing owns its own
								// settings — §1.1 says hide the panel then). Without this the column
								// still claimed its 400px and left a dead band beside Protective
								// Actions. `:empty` matches a div whose child rendered null, and
								// `display:none` takes it out of the flex row entirely, so the blocks
								// on the left go back to full width. Same idiom as the drawers' footer
								// slots.
								<div className="lg:min-h-[134px] lg:w-[400px] lg:shrink-0 empty:hidden">
									{asidePanel}
								</div>
							) : null}
						</div>
					</div>

					<Divider />

					<div
						className={`flex w-full max-w-[360px] flex-col gap-1.5 ${LOCK_FADE}`}
					>
						<SectionLabel
							title="Advertising region"
							helpLabel="About advertising regions"
							help="Traffic outside your advertising regions can be evaluated more strictly."
							disabled={locked}
						/>
						<RegionPicker
							value={region}
							onChange={(next) => {
								setRegion(next)
								touch()
							}}
							ariaLabel="Advertising region"
							disabled={locked}
							invalid={regionEmpty}
							errorId={REGION_ERROR_ID}
							compact={bare}
						/>
						{/* Only once the region is actually empty, and never while the
					    master switch is off: a red line under a greyed-out field is
					    an error about a control you cannot reach. */}
						{regionEmpty && (
							<p
								id={REGION_ERROR_ID}
								role="alert"
								className="text-[13px] leading-[18px] text-[rgb(var(--cg-danger))]"
							>
								{REGION_EMPTY_MESSAGE}
							</p>
						)}
					</div>

					<Divider />

					<div className={`flex flex-col gap-3 ${LOCK_FADE}`}>
						<SectionLabel
							title="Protection settings"
							helpLabel="About protection settings"
							help="Choose the bad traffic signals our system should flag or not."
							helpDescription={DETECTION_SCOPE_MESSAGE}
							disabled={locked}
						/>
						{/* The mode choice sits ABOVE the box, not inside it (Esteban, 08-12:
					    "bring the 2 options (automatic and custom) outside and above the gray
					    container"). It reads as the switch for the box under it, the same way
					    the master toggle at the top reads as the switch for the whole card.
					    What stays in the box is what the box is now titled after: the level
					    (the slider) and the signals (the toggle grid).
					    `max-w` only caps, it never narrows, so the drawer needs no `bare`
					    branch here: at drawer width the group simply takes the width it is
					    given. 496px is the measure the radios have on the full page, rounded up
					    from the 495.5px column they sat in. With no cap the line runs the full
					    content width, which is about 180 characters at 12px out there. */}
						<div className="max-w-[496px]">
							<RadioGroup
								name="sensitivity-mode"
								value={sensitivityMode}
								// One prop for the whole set: RadioGroup carries `disabled` down
								// through its context, so every Radio in it goes out together and the
								// DS paints its own 50% on each box.
								disabled={locked}
								onValueChange={(value) => {
									setSensitivityMode(value as SensitivityMode)
									touch()
								}}
							>
								<RadioOption
									value="automatic"
									id="sensitivity-automatic"
									label="Automatic"
									recommended
									description={AUTOMATIC_DESCRIPTION}
									disabled={locked}
								/>
								<RadioOption
									value="custom"
									id="sensitivity-custom"
									label="Custom"
									description={CUSTOM_DESCRIPTION}
									disabled={locked}
								/>
							</RadioGroup>
						</div>
						<div className="rounded-xl border border-[rgb(var(--cg-border))] bg-[rgb(var(--cg-surface-subtle))] p-4">
							{/* Dims by hand for the same reason the radio labels do: it is plain
						    text, so no DS control fades it when the master switch goes off.
						    `mb-4` is the same 16px the sensitivity block used to spend under its
						    own rule, so the head sits the same distance above the body whether
						    that body starts with the slider (Custom) or the signals (Automatic). */}
							<span
								className={`mb-4 block text-base font-medium text-[rgb(var(--cg-text-primary))] transition-opacity ${
									locked ? 'opacity-50' : ''
								}`}
							>
								{SETTINGS_BOX_TITLE}
							</span>
							{/* The level, and only on Custom. Automatic owns the level itself, so
						    there is nothing to show and nothing to rule off: the box closes to its
						    title and its signal grid. The rule and the gap under it therefore live
						    INSIDE the collapse, never outside it - outside, Automatic would leave a
						    rule hanging under the title with no block above it.
						    The old two-column row is gone with the radios, and the `bare` branches
						    that flattened it went with it: one column is one column at every width,
						    in the drawer and on the page alike. */}
							<Collapse open={sensitivityMode === 'custom'}>
								{/* The gap under the rule is PADDING here, never a margin on the block
							    below. The DS Collapse animates to the panel's `scrollHeight`, and the
							    bottom margin of a last child is not reliably part of that number, so a
							    margin can measure short and close the gap the moment the panel settles. */}
								<div className="pb-4">
									{/* `pb-5` clears the cut as well as spacing the rule. The DS Collapse
								    panel is `overflow: hidden`, and the Slider's 18px thumb hangs 4px
								    below its own 10px track row, so a panel that stops at the tick row
								    shaves the bottom off the thumb. Measured live before the fix: the
								    panel read clientHeight 78 against scrollHeight 82.
								    The entry and exit animation hangs off `data-open`, and the keyframes
								    live in protection-settings-card.css next to the tray collapse they
								    match. `data-open` reads the MODE, not the Collapse. The Collapse keeps
								    this panel mounted for the length of its close, and that is the window
								    the exit animation runs in. */}
									<div
										className="protection-card-sensitivity-body flex flex-col gap-2 border-b border-[rgb(var(--cg-border))] pb-5"
										data-open={sensitivityMode === 'custom'}
									>
										<span
											className={`text-[12px] font-medium leading-4 text-[rgb(var(--cg-text-primary))] transition-opacity ${
												locked ? 'opacity-50' : ''
											}`}
										>
											{SENSITIVITY_LABEL}
										</span>
										{/* The DS `Slider`, not a hand-rolled range input: it already owns the
									    track, the brand fill, the 18px thumb, and the native input
									    underneath that carries the keyboard and the screen-reader
									    behaviour.
									    It does need this wrapper to fade. Every other DS control on the
									    page paints its own `cg-opacity-50` when disabled; Slider does not,
									    because its input is already `opacity-0` and the visible parts are
									    plain divs. So `disabled` stops the interaction and the wrapper is
									    what says so on screen. */}
										<div
											className={`transition-opacity ${
												locked ? 'opacity-50' : ''
											}`}
										>
											<Slider
												id="aggressiveness"
												aria-label={SENSITIVITY_LABEL}
												// The scale is named, so the reader has to say the name. Without this
												// it announces the raw number and the eye and the ear disagree.
												aria-valuetext={aggressivenessName(aggressiveness)}
												min={1}
												max={AGGRESSIVENESS_LEVELS.length}
												step={1}
												value={aggressiveness}
												trackWidth={AGGRESSIVENESS_TRACK}
												// No floating tooltip and no `minLabel` / `maxLabel`. All three named
												// the stop you were already on, which is the one thing the scale did
												// not need: Esteban could not see the OTHER four levels without
												// dragging through them (08-11). The tick row below names all five at
												// once, so the tooltip would only repeat one of them and cover it.
												hideTooltip
												disabled={locked}
												onChange={(value) => {
													setAggressiveness(value)
													touch()
												}}
											/>
											{/* The scale, spelled out. Each word sits on its own stop, so the
										    reader can pick a level by name instead of dragging to find it.
										    `h-4` because the words are absolutely placed and would otherwise
										    give the row no height at all; `mt-2` because the thumb hangs 4px
										    below its track and would sit on the words at `mt-1`. */}
											<div
												aria-hidden="true"
												className="relative mt-2 h-4"
												style={{ width: AGGRESSIVENESS_TRACK }}
											>
												{AGGRESSIVENESS_LEVELS.map((name, index) => (
													<span
														key={name}
														// The word on the current stop is the value read-out now that the
														// tooltip is gone, so it takes the brand colour and the weight. The
														// other four step back one level, to secondary.
														// Secondary, NOT tertiary. Tertiary measured #9F9EA3 here, which is
														// about 2.6:1 on this surface. These five words ARE the scale, and
														// reading the scale at a glance is the whole point of the row.
														// Tertiary is the level for text a reader may skip.
														className={`absolute top-0 whitespace-nowrap text-[11px] leading-4 ${
															index + 1 === aggressiveness
																? 'font-medium text-[rgb(var(--cg-brand))]'
																: 'text-[rgb(var(--cg-text-secondary))]'
														}`}
														style={aggressivenessTickStyle(index)}
													>
														{name}
													</span>
												))}
											</div>
										</div>
									</div>
								</div>
							</Collapse>

							{/* The head of the grid, in the same 12px medium the Sensitivity label
						    uses, so the two halves of the box read as one pair. `mb-2` is the
						    same 8px the slider keeps under its own label.
						    It sits OUTSIDE the Collapse: the grid stands on both modes, so its
						    head must stand on both too. On Automatic it is the first thing under
						    the box title; on Custom it follows the rule and its 16px of padding. */}
							<span
								className={`mb-2 block text-[12px] font-medium leading-4 text-[rgb(var(--cg-text-primary))] transition-opacity ${
									locked ? 'opacity-50' : ''
								}`}
							>
								{SIGNALS_LABEL}
							</span>
							<div
								className={`flex flex-col gap-3.5 ${
									bare ? '' : 'lg:flex-row lg:gap-8'
								}`}
							>
								<div className="flex flex-1 flex-col gap-3.5">
									{checkColumns[0].map(renderCheck)}
								</div>
								{/* The rule only exists once the checks are actually side by
							    side - below lg they stack and there is no gutter to
							    divide. */}
								<Divider
									orientation="vertical"
									className={bare ? 'hidden' : 'hidden lg:block'}
								/>
								<div className="flex flex-1 flex-col gap-3.5">
									{checkColumns[1].map(renderCheck)}
								</div>
							</div>

							<div className="mt-5 border-t border-[rgb(var(--cg-border))] pt-4">
								<Toggle
									checked={whitelistOn}
									onChange={() => {
										setWhitelistOn((on) => !on)
										touch()
									}}
									label="Whitelist selected IP addresses"
									description="Whitelisted IPs are never flagged or blocked."
									disabled={locked}
									size="md"
									className="!items-center !gap-3"
								/>
								{/* The second toggle-driven reveal on this card, so it plays the
							    same way as the first. */}
								<Collapse open={whitelistOn}>
									<div className="mt-3 max-w-md">
										<Textarea
											aria-label="Whitelisted IP addresses"
											placeholder={'203.0.113.4\n198.51.100.0/24'}
											helperText="One IP address or CIDR range per line."
											rows={3}
											// DS default is `md` (16px) — one step up from every
											// label in this panel. `sm` puts the field at the same
											// 14px as the check labels it sits under.
											size="sm"
											disabled={locked}
											value={whitelist}
											onChange={(event) => {
												setWhitelist(event.target.value)
												touch()
											}}
										/>
									</div>
								</Collapse>
							</div>
						</div>
					</div>

					{/* DRAWER mode. The footer is pinned, so Save stays on screen however far
				    the panel scrolls (Esteban, 08-13).
				    The row STAYS, ticked or not, because the tick is itself a thing to
				    save now; it only ever changes its disabled state.
				    `null` is the one frame before the drawer's footer node has mounted. */}
					{saveSlot !== undefined && saveSlot !== null
						? createPortal(saveRow, saveSlot)
						: null}
				</div>
			</Frame>
		</>
	)
}
